import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button } from '@Comp';
import { FaInfoCircle } from 'react-icons/fa';
import FaceVerification from './FaceVerification';
import { VerifyNow } from '../../../services/requests';
import './style.scss';

@inject('commonStore', 'onboardingStore')
@withRouter
@observer
export default class OnboardingStep1 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            verificationType: 'id_number', // 'id_number' or 'upload_document'
            idNumber: '',
            showFaceVerification: false,
            faceVerificationComplete: false,
            verificationData: null,
            apiError: null,
        };
    }

    componentDidMount() {
        // Load existing data from store if user returns to this step
        const { onboardingStore } = this.props;
        if (onboardingStore.identityData.idNumber) {
            this.setState({
                idNumber: onboardingStore.identityData.idNumber,
                faceVerificationComplete: onboardingStore.identityData.verified,
                verificationData: onboardingStore.identityData.faceVerification,
            });
        }
    }

    handleVerificationTypeChange = (type) => {
        this.setState({ verificationType: type });
    };

    handleIdNumberChange = (e) => {
        const idNumber = e.target.value;
        const { onboardingStore } = this.props;

        // Save to store
        onboardingStore.setIdNumber(idNumber);

        this.setState({
            idNumber,
            showFaceVerification: false,
            faceVerificationComplete: false,
        });
    };

    handleProceedToFaceVerification = () => {
        const { idNumber } = this.state;

        // Validate ID number (13 digits)
        if (!idNumber || idNumber.length !== 13) {
            alert('Please enter a valid 13-digit SA ID number');
            return;
        }

        this.setState({ showFaceVerification: true });
    };

    handleFaceVerificationComplete = async (idNumber, selfieImage) => {
        try {
            // Clear previous errors
            this.setState({ apiError: null });

            // Call VerifyNow API
            const response = await VerifyNow.faceMatch({
                idNumber,
                selfieImage
            });

            // Return the response to FaceVerification component
            return response.data;
        } catch (error) {
            console.error('Face verification error:', error);

            // Set error state (non-blocking)
            this.setState({ apiError: 'Something went wrong. You can skip this step.' });

            throw new Error('Something went wrong. You can skip this step.');
        }
    };

    handleVerificationSuccess = (verificationData) => {
        const { onboardingStore } = this.props;

        // Save verification data to store
        onboardingStore.setFaceVerification(verificationData);

        this.setState({
            faceVerificationComplete: true,
            verificationData,
        });
    };

    handleSkip = () => {
        // Clear errors when skipping
        this.setState({ apiError: null });
        // Skip Step 1 and Step 2, go directly to Step 3
        this.props.history.push('/home/onboarding/step3');
    };

    handleNext = () => {
        const { verificationType, idNumber, faceVerificationComplete } = this.state;
        const { onboardingStore } = this.props;

        if (verificationType === 'id_number') {
            if (!idNumber) {
                alert('Please enter your SA ID number');
                return;
            }

            if (!faceVerificationComplete) {
                alert('Please complete face verification to continue');
                return;
            }
        }

        // Mark Step 1 as complete in the store
        onboardingStore.completeStep1();

        // Navigate to Step 2 (data is now in store, no need to pass via state)
        this.props.history.push('/home/onboarding/step2');
    };

    render() {
        const { verificationType, idNumber, showFaceVerification, faceVerificationComplete, apiError } = this.state;

        return (
            <div className="step-content">
                <h3 className="step-title">Lets verify your identity</h3>
                <p className="step-description">Lets get to know you better</p>

                {/* API Error Message (non-blocking) */}
                {apiError && (
                    <div className="alert alert-warning" role="alert" style={{ marginBottom: '20px' }}>
                        <FaInfoCircle style={{ marginRight: '8px' }} />
                        {apiError}
                    </div>
                )}

                {/* Verification Options */}
                <div className="verification-options">
                    {/* SA ID Number Option */}
                    <div className="verification-option">
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="verificationType"
                                value="id_number"
                                checked={verificationType === 'id_number'}
                                onChange={() => this.handleVerificationTypeChange('id_number')}
                            />
                            <span className="radio-text">SA ID number</span>
                        </label>

                        {verificationType === 'id_number' && (
                            <div className="input-container">
                                <input
                                    type="text"
                                    className="form-control id-input"
                                    placeholder="Enter your SA ID number"
                                    value={idNumber}
                                    onChange={this.handleIdNumberChange}
                                    maxLength="13"
                                />
                                {idNumber.length === 13 && !showFaceVerification && (
                                    <Button
                                        color="primary"
                                        onClick={this.handleProceedToFaceVerification}
                                        className="mt-3"
                                    >
                                        Proceed to Face Verification
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Upload Document Option */}
                    <div className="verification-option">
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="verificationType"
                                value="upload_document"
                                checked={verificationType === 'upload_document'}
                                onChange={() => this.handleVerificationTypeChange('upload_document')}
                            />
                            <span className="radio-text">Upload a certified ID copy</span>
                        </label>

                        {verificationType === 'upload_document' && (
                            <div className="upload-container">
                                <Button color="secondary" outline size="sm">
                                    Choose File
                                </Button>
                                <span className="upload-text">No file chosen</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Face Verification Component */}
                {verificationType === 'id_number' && showFaceVerification && (
                    <FaceVerification
                        idNumber={idNumber}
                        onVerificationComplete={this.handleFaceVerificationComplete}
                        onSuccess={this.handleVerificationSuccess}
                    />
                )}

                {/* Info Box */}
                <div className="info-box">
                    <FaInfoCircle className="info-icon" />
                    <div className="info-text">
                        <span>Only the business owner can do this. Other team members can be given access thereafter. </span>
                        <a href="#" className="info-link">Learn how</a>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    <Button
                        color="secondary"
                        outline
                        onClick={this.handleSkip}
                    >
                        Skip
                    </Button>
                    <Button
                        color="primary"
                        onClick={this.handleNext}
                        disabled={
                            verificationType === 'id_number' &&
                            (!idNumber || !faceVerificationComplete)
                        }
                    >
                        Next
                    </Button>
                </div>
            </div>
        );
    }
}
